import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, TextField, Button, Grid,
  FormControlLabel, Checkbox, Alert, Stepper, Step, StepLabel,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider, CircularProgress,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PlaceIcon from '@mui/icons-material/Place';
import carpoolService from '../../services/carpool.service';
import MapboxAddressInput from '../../components/MapboxAddressInput';
import MiniMap from '../../components/MiniMap';

const steps = ['Itinéraire', 'Véhicule', 'Photos du véhicule', 'Documents', 'Confirmation'];

const daysOfWeek = [
  { value: 'monday', label: 'Lundi' },
  { value: 'tuesday', label: 'Mardi' },
  { value: 'wednesday', label: 'Mercredi' },
  { value: 'thursday', label: 'Jeudi' },
  { value: 'friday', label: 'Vendredi' },
  { value: 'saturday', label: 'Samedi' },
  { value: 'sunday', label: 'Dimanche' },
];

const vehiclePhotos = [
  { field: 'photoArriere', label: 'Arrière du véhicule', helperText: "Photo de l'arrière complet" },
  { field: 'photoDroit', label: 'Côté droit', helperText: 'Photo du côté droit complet' },
  { field: 'photoGauche', label: 'Côté gauche', helperText: 'Photo du côté gauche complet' },
  { field: 'photoInterieur', label: 'Intérieur', helperText: "Photo de l'intérieur (sièges, habitacle)" },
  { field: 'photoTableauBord', label: 'Tableau de bord', helperText: 'Photo du tableau de bord et compteur' },
  { field: 'photoParechoc', label: 'Pare-choc', helperText: 'Photo du pare-choc avant' },
];

const CarpoolCreate = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    departure_address: '',
    departure_latitude: null,
    departure_longitude: null,
    arrival_address: '',
    arrival_latitude: null,
    arrival_longitude: null,
    departure_time: '07:00',
    available_seats: 3,
    price_per_seat: '',
    is_recurring: false,
    recurring_days: [],
    preferences: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    vehicle_plate: '',
    fuel: 'Essence',
  });

  const [files, setFiles] = useState({
    photoArriere: null,
    photoDroit: null,
    photoGauche: null,
    photoInterieur: null,
    photoTableauBord: null,
    photoParechoc: null,
    identity: null,
    permisConduire: null,
    carteGrise: null,
    insuranceDoc: null,
    controleTechnique: null,
  });

  const currentYear = new Date().getFullYear();
  const needsControle = form.vehicle_year && (currentYear - parseInt(form.vehicle_year)) >= 4;
  const allPhotosUploaded = vehiclePhotos.every(p => files[p.field]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleFile = (field, file) => {
    if (file) setFiles({ ...files, [field]: file });
  };

  const handleDayToggle = (day) => {
    const days = form.recurring_days.includes(day)
      ? form.recurring_days.filter(d => d !== day)
      : [...form.recurring_days, day];
    setForm({ ...form, recurring_days: days });
  };

  const canNext = () => {
    switch (activeStep) {
      case 0: return form.departure_address && form.arrival_address && form.price_per_seat;
      case 1: return form.vehicle_brand && form.vehicle_model && form.vehicle_year && form.vehicle_plate;
      case 2: return allPhotosUploaded;
      case 3: return files.identity && files.permisConduire && files.carteGrise && files.insuranceDoc && (!needsControle || files.controleTechnique);
      default: return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          if (key === 'recurring_days') {
            formData.append(key, JSON.stringify(val));
          } else {
            formData.append(key, val);
          }
        }
      });
      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });

      await carpoolService.create(formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const FileUploadBox = ({ label, field, required, helperText }) => (
    <Button
      component="label"
      fullWidth
      sx={{
        p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column',
        border: files[field] ? '2px solid rgba(240,235,224,0.5)' : '2px dashed rgba(255,255,255,0.2)',
        bgcolor: files[field] ? 'rgba(240,235,224,0.05)' : 'rgba(255,255,255,0.02)',
        textAlign: 'center', textTransform: 'none', color: 'white',
        transition: 'all 0.3s',
        '&:hover': { borderColor: 'rgba(240,235,224,0.4)', bgcolor: 'rgba(255,255,255,0.04)' },
      }}
    >
      <input
        type="file"
        accept="image/*,.pdf"
        hidden
        onChange={(e) => { handleFile(field, e.target.files[0]); e.target.value = ''; }}
      />
      {files[field] ? (
        <>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#F0EBE0', mb: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#F0EBE0' }}>
            {files[field].name}
          </Typography>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', mb: 1 }} />
          <Typography variant="body2" fontWeight={600}>{label} {required && '*'}</Typography>
          {helperText && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
              {helperText}
            </Typography>
          )}
        </>
      )}
    </Button>
  );

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Proposer un <span style={{ color: '#F0EBE0' }}>trajet</span>
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', mb: 4 }}>
          Partagez vos trajets à Mayotte. Fournissez les infos de votre véhicule et vos documents pour commencer.
        </Typography>

        {success ? (
          <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(240,235,224,0.3)' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#F0EBE0', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                Trajet créé avec succès !
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Vos documents sont en cours de vérification. Votre trajet sera visible une fois approuvé.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/carpool/bookings')}
                sx={{ background: 'linear-gradient(135deg, #2D4A35, #1E3525)', fontWeight: 700, borderRadius: 3, textTransform: 'none', mr: 2 }}
              >
                Voir mes trajets
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/carpool')}
                sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, borderRadius: 3, textTransform: 'none' }}
              >
                Retour au covoiturage
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{
              mb: 4,
              '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.75rem' },
              '& .MuiStepLabel-label.Mui-active': { color: '#F0EBE0' },
              '& .MuiStepLabel-label.Mui-completed': { color: '#F0EBE0' },
              '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.1)' },
              '& .MuiStepIcon-root.Mui-active': { color: '#F0EBE0' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#F0EBE0' },
            }}>
              {steps.map(label => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            <Card sx={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(30px)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <CardContent sx={{ p: 4 }}>

                {/* Step 0: Itinéraire */}
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                      Itinéraire et détails du trajet
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Adresses style Uber avec ligne verticale */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 3.5, pb: 1 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 12, color: '#F0EBE0' }} />
                            <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(240,235,224,0.2)', my: 0.5, minHeight: 40 }} />
                            <PlaceIcon sx={{ fontSize: 18, color: '#F0EBE0' }} />
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <MapboxAddressInput
                              label="Départ"
                              placeholder="D'où partez-vous ?"
                              value={form.departure_address}
                              onSelect={(result) => {
                                if (!result) { setForm(p => ({ ...p, departure_address: '', departure_latitude: null, departure_longitude: null })); return; }
                                setForm(p => ({ ...p, departure_address: result.fullAddress || result.place_name || result.address, departure_latitude: result.latitude, departure_longitude: result.longitude }));
                              }}
                              icon={null}
                              showGeolocate
                              sx={{
                                '& .MuiInputBase-root': { color: 'white' },
                                '& input::placeholder': { color: 'rgba(255,255,255,0.4) !important' },
                              }}
                            />
                            <MapboxAddressInput
                              label="Destination"
                              placeholder="Où allez-vous ?"
                              value={form.arrival_address}
                              onSelect={(result) => {
                                if (!result) { setForm(p => ({ ...p, arrival_address: '', arrival_latitude: null, arrival_longitude: null })); return; }
                                setForm(p => ({ ...p, arrival_address: result.fullAddress || result.place_name || result.address, arrival_latitude: result.latitude, arrival_longitude: result.longitude }));
                              }}
                              icon={null}
                              showGeolocate={false}
                              sx={{
                                '& .MuiInputBase-root': { color: 'white' },
                                '& input::placeholder': { color: 'rgba(255,255,255,0.4) !important' },
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Mini carte preview */}
                        {form.departure_latitude && form.arrival_latitude && (
                          <Box sx={{ mt: 2 }}>
                            <MiniMap
                              pickupLat={form.departure_latitude}
                              pickupLng={form.departure_longitude}
                              dropoffLat={form.arrival_latitude}
                              dropoffLng={form.arrival_longitude}
                              height={200}
                              onPickupDragEnd={({ latitude, longitude }) => {
                                setForm(p => ({ ...p, departure_latitude: latitude, departure_longitude: longitude }));
                              }}
                              onDropoffDragEnd={({ latitude, longitude }) => {
                                setForm(p => ({ ...p, arrival_latitude: latitude, arrival_longitude: longitude }));
                              }}
                            />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Heure de départ" type="time" value={form.departure_time}
                          onChange={(e) => handleChange('departure_time', e.target.value)} InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField fullWidth label="Places disponibles" type="number" value={form.available_seats}
                          onChange={(e) => handleChange('available_seats', e.target.value)}
                          inputProps={{ min: 1, max: 8 }} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField fullWidth label="Prix/place (€) *" type="number" value={form.price_per_seat}
                          onChange={(e) => handleChange('price_per_seat', e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Préférences" value={form.preferences}
                          onChange={(e) => handleChange('preferences', e.target.value)}
                          multiline rows={2} placeholder="Ex: Non-fumeur, Musique calme" />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox checked={form.is_recurring}
                              onChange={(e) => handleChange('is_recurring', e.target.checked)}
                              sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#F0EBE0' } }} />
                          }
                          label="Trajet récurrent"
                        />
                        {form.is_recurring && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {daysOfWeek.map((day) => (
                              <Chip
                                key={day.value}
                                label={day.label}
                                onClick={() => handleDayToggle(day.value)}
                                sx={{
                                  bgcolor: form.recurring_days.includes(day.value) ? 'rgba(240,235,224,0.15)' : 'rgba(255,255,255,0.05)',
                                  color: form.recurring_days.includes(day.value) ? '#F0EBE0' : 'rgba(255,255,255,0.5)',
                                  border: form.recurring_days.includes(day.value) ? '1px solid rgba(240,235,224,0.4)' : '1px solid rgba(255,255,255,0.1)',
                                  fontWeight: 600, cursor: 'pointer',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Step 1: Véhicule */}
                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      Informations du véhicule
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
                      Ces informations permettent aux passagers de reconnaître votre véhicule.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Marque *" value={form.vehicle_brand}
                          onChange={(e) => handleChange('vehicle_brand', e.target.value)} placeholder="Ex: Toyota" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Modèle *" value={form.vehicle_model}
                          onChange={(e) => handleChange('vehicle_model', e.target.value)} placeholder="Ex: Corolla" />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField fullWidth label="Année *" type="number" value={form.vehicle_year}
                          onChange={(e) => handleChange('vehicle_year', e.target.value)}
                          inputProps={{ min: 1990, max: currentYear }} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField fullWidth label="Couleur" value={form.vehicle_color}
                          onChange={(e) => handleChange('vehicle_color', e.target.value)} placeholder="Ex: Grise" />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Plaque d'immatriculation *" value={form.vehicle_plate}
                          onChange={(e) => handleChange('vehicle_plate', e.target.value)} placeholder="Ex: AB-123-CD" />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Carburant</InputLabel>
                          <Select value={form.fuel} onChange={(e) => handleChange('fuel', e.target.value)} label="Carburant">
                            <MenuItem value="Essence">Essence</MenuItem>
                            <MenuItem value="Diesel">Diesel</MenuItem>
                            <MenuItem value="Électrique">Électrique</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {form.vehicle_year && needsControle && (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        Votre véhicule a plus de 4 ans — le contrôle technique sera obligatoire à l'étape Documents.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Step 2: Photos du véhicule */}
                {activeStep === 2 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      Photos du véhicule
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                      6 photos obligatoires pour l'état des lieux du véhicule
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', mb: 3 }}>
                      Formats acceptés : JPG, PNG, WEBP (max 10 Mo par photo)
                    </Typography>

                    <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(240,235,224,0.05)', border: '1px solid rgba(240,235,224,0.15)' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhotoCameraIcon sx={{ color: '#F0EBE0', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#F0EBE0' }}>
                          {vehiclePhotos.filter(p => files[p.field]).length} / {vehiclePhotos.length} photos ajoutées
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      {vehiclePhotos.map((photo) => (
                        <Grid item xs={12} sm={6} key={photo.field}>
                          <Button
                            component="label"
                            fullWidth
                            sx={{
                              p: 0, borderRadius: 2, display: 'flex', flexDirection: 'column',
                              border: files[photo.field] ? '2px solid rgba(240,235,224,0.5)' : '2px dashed rgba(255,255,255,0.2)',
                              bgcolor: files[photo.field] ? 'rgba(240,235,224,0.05)' : 'rgba(255,255,255,0.02)',
                              overflow: 'hidden', textTransform: 'none', color: 'white',
                              transition: 'all 0.3s', minHeight: 0,
                              '&:hover': { borderColor: 'rgba(240,235,224,0.4)', bgcolor: 'rgba(255,255,255,0.04)' },
                            }}
                          >
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => { handleFile(photo.field, e.target.files[0]); e.target.value = ''; }}
                            />
                            {files[photo.field] ? (
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ width: '100%', height: 140, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.3)' }}>
                                  <img
                                    src={URL.createObjectURL(files[photo.field])}
                                    alt={photo.label}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </Box>
                                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CheckCircleIcon sx={{ fontSize: 18, color: '#F0EBE0' }} />
                                  <Typography variant="caption" fontWeight={600} sx={{ color: '#F0EBE0' }}>
                                    {photo.label}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                                <PhotoCameraIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.25)', mb: 1 }} />
                                <Typography variant="body2" fontWeight={600}>{photo.label} *</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                                  {photo.helperText}
                                </Typography>
                              </Box>
                            )}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Step 3: Documents */}
                {activeStep === 3 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      Documents requis
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
                      Formats acceptés : JPG, PNG, PDF (max 10 Mo)
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FileUploadBox label="Pièce d'identité" field="identity" required helperText="CNI, passeport ou titre de séjour" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FileUploadBox label="Permis de conduire" field="permisConduire" required helperText="Permis en cours de validité" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FileUploadBox label="Carte grise" field="carteGrise" required helperText="Certificat d'immatriculation" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FileUploadBox label="Attestation d'assurance" field="insuranceDoc" required helperText="Assurance en cours de validité" />
                      </Grid>
                      {needsControle && (
                        <Grid item xs={12} sm={6}>
                          <FileUploadBox label="Contrôle technique" field="controleTechnique" required helperText="Obligatoire pour les véhicules de + de 4 ans" />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Step 4: Confirmation */}
                {activeStep === 4 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                      Récapitulatif
                    </Typography>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', p: 3, borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Trajet</Typography>
                          <Typography fontWeight={600}>{form.departure_address}</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>→ {form.arrival_address}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Départ</Typography>
                          <Typography fontWeight={600}>{form.departure_time}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Places</Typography>
                          <Typography fontWeight={600}>{form.available_seats}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Prix/place</Typography>
                          <Typography fontWeight={600} sx={{ color: '#F0EBE0' }}>{form.price_per_seat}€</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Récurrent</Typography>
                          <Typography fontWeight={600}>{form.is_recurring ? 'Oui' : 'Non'}</Typography>
                        </Grid>
                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} /></Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Véhicule</Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <DirectionsCarIcon sx={{ color: '#F0EBE0', fontSize: 20 }} />
                            <Typography fontWeight={600}>
                              {form.vehicle_brand} {form.vehicle_model} {form.vehicle_color} ({form.vehicle_year}) — {form.vehicle_plate}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} /></Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Photos du véhicule</Typography>
                          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                            {vehiclePhotos.map(p => (
                              <Chip key={p.field} icon={<PhotoCameraIcon />} label={p.label} size="small"
                                sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Documents fournis</Typography>
                          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                            <Chip icon={<DescriptionIcon />} label="Pièce d'identité" size="small" sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            <Chip icon={<DescriptionIcon />} label="Permis de conduire" size="small" sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            <Chip icon={<DescriptionIcon />} label="Carte grise" size="small" sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            <Chip icon={<DescriptionIcon />} label="Assurance" size="small" sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            {needsControle && (
                              <Chip icon={<DescriptionIcon />} label="Contrôle technique" size="small" sx={{ bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0' }} />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                      Après soumission, nos équipes vérifieront vos documents sous 24 à 48h. Votre trajet sera visible une fois approuvé.
                    </Alert>
                  </Box>
                )}

              </CardContent>
            </Card>

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                onClick={() => activeStep === 0 ? navigate('/carpool') : setActiveStep(s => s - 1)}
                sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'none' }}
              >
                Retour
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(s => s + 1)}
                  disabled={!canNext()}
                  sx={{
                    background: 'linear-gradient(135deg, #2D4A35, #1E3525)',
                    fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none',
                    '&:disabled': { background: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                  sx={{
                    background: 'linear-gradient(135deg, #2D4A35, #1E3525)',
                    fontWeight: 700, px: 4, borderRadius: 3, textTransform: 'none',
                    boxShadow: '0 8px 30px rgba(240,235,224,0.3)',
                  }}
                >
                  {loading ? 'Création...' : 'Créer mon trajet'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default CarpoolCreate;
